import React, { useState } from 'react';

import { ExpandLess, ExpandMore } from '@mui/icons-material';

import {
  CreatureLimitation,
  LootPattern,
  LootPatternBand,
  LootTable,
} from "@dndModels";
import { CREATURE_CRS, ITEM_TYPES, RARITY_NUM_TO_STR_MAP, TRUE_DAMAGE_TYPES } from "@dndModels/Constants";
import { DamageType } from "@dndModels/Types";
import {
  DEFAULT_BANDS,
  DamageTypePreview,
  MAGICAL_DAMAGE_TYPES,
  bandLabel,
  planEntries,
  previewPattern,
} from "@dndUtilities/loot_patterns";
import {
  LootAnalysis,
  analyzeLootData,
  cachedLootAnalysis,
  creatureFactsFor,
  entriesForPattern,
} from "@dndUtilities/loot_analysis";

import StringBox from "@components/input/strings/StringBox";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import CheckBox from "@components/input/CheckBox";
import ProgressBar from "@components/input/ProgressBar";
import DeleteButton from "@components/input/buttons/icon_buttons/DeleteButton";
import CopyButton from "@components/input/buttons/icon_buttons/CopyButton";
import CreatureLimitInput from "@dndComponents/limitations/CreatureLimitInput";
import { DiceRollFields } from "@dndComponents/other/LootEntryInput";
import DandDButtonBox from '@dndComponents/DandDButtonBoxSimple';
import FlexBox from '@components/layout/FlexBox';

interface Props {
  table: LootTable;
  disabled: boolean;
  onChange: (updater: (o: LootTable) => void) => void;
}

const CR_OPTIONS = CREATURE_CRS.filter(o => +o <= 30);
const RARITY_OPTIONS = ["0","1","2","3","4","5"].map(o => ({ id: o, text: RARITY_NUM_TO_STR_MAP[o] }));
const LIST_LIMIT = 25;

const crString = (cr: number) => {
  switch (cr) {
    case 0.125: return "1/8";
    case 0.25: return "1/4";
    case 0.5: return "1/2";
    default: return `${cr}`;
  }
}

const newPattern = (name: string, item_relation: "Adds" | "Protects Against", item_types: string[]) => {
  const pattern = new LootPattern();
  pattern.name = name;
  pattern.damage_types = [...MAGICAL_DAMAGE_TYPES] as DamageType[];
  pattern.creature_relation = "Deals";
  pattern.item_relation = item_relation;
  pattern.item_types = item_types;
  pattern.bands = DEFAULT_BANDS.map(o => new LootPatternBand(o));
  return pattern;
}

// Builds coin & item entries in bulk from patterns that pair creatures with items by damage type.
export default function LootPatternsTab({
  table,
  disabled,
  onChange,
}: Props) {
  const [analysis, setAnalysis] = useState<LootAnalysis | null>(cachedLootAnalysis());
  const [progress, setProgress] = useState<{ message: string, fraction: number } | null>(null);
  const [expanded, setExpanded] = useState<string[]>([]);
  const [message, setMessage] = useState("");

  const toggle = (key: string) => {
    setExpanded(expanded.includes(key) ? expanded.filter(o => o !== key) : [...expanded, key]);
  };

  const analyze = async () => {
    setProgress({ message: "Starting", fraction: 0 });
    try {
      const result = await analyzeLootData((progress_message, fraction) => setProgress({ message: progress_message, fraction }));
      setAnalysis(result);
    } finally {
      setProgress(null);
    }
  };

  const updatePattern = (true_id: string, update: (pattern: LootPattern) => void) => {
    onChange(o => {
      const pattern = o.patterns.find(p => p.true_id === true_id);
      if (pattern) {
        update(pattern);
      }
    });
  };

  const updateBand = (pattern_id: string, band_id: string, update: (band: LootPatternBand) => void) => {
    updatePattern(pattern_id, p => {
      const band = p.bands.find(b => b.true_id === band_id);
      if (band) {
        update(band);
      }
    });
  };

  const generate = (pattern: LootPattern) => {
    if (!analysis) {
      return;
    }
    const planned = planEntries(pattern, creatureFactsFor(analysis, pattern), analysis.items);
    const entries = entriesForPattern(pattern, planned);
    const replaced = table.entries.filter(o => o.pattern_id === pattern.true_id).length;
    onChange(o => {
      o.entries = [...o.entries.filter(e => e.pattern_id !== pattern.true_id), ...entries];
    });
    setMessage(`${pattern.name}: made ${entries.length} entries${replaced > 0 ? `, replacing ${replaced}` : ""}. They're in the Coins & Items tab.`);
  };

  const renderBands = (pattern: LootPattern) => (
    <FlexBox direction="column">
      <FlexBox className="header">
        CR Bands
      </FlexBox>
      <FlexBox className="small_label gray">
        Each band becomes its own entry for each damage type. The CR is the creature&apos;s,
        and the rarity range limits which items it can give.
      </FlexBox>
      { pattern.bands.map(band => (
        <FlexBox key={band.true_id}>
          <FlexBox direction="row">
            <FlexBox className="centered" xs_expand={0}>
              <CheckBox
                value={band.enabled}
                disabled={disabled}
                onChange={(changed: boolean) => updateBand(pattern.true_id, band.true_id, b => { b.enabled = changed; })}
              />
            </FlexBox>
            <FlexBox xs_expand={0} style={{ width: "15%" }}>
              <SelectStringBox name="Min CR" options={CR_OPTIONS} value={`${band.min_cr}`} auto_sort={false} disabled={disabled}
                onChange={(changed: string) => updateBand(pattern.true_id, band.true_id, b => { b.min_cr = +changed; })}
              />
            </FlexBox>
            <FlexBox xs_expand={0} style={{ width: "15%" }}>
              <SelectStringBox name="Max CR" options={CR_OPTIONS} value={`${band.max_cr}`} auto_sort={false} disabled={disabled}
                onChange={(changed: string) => updateBand(pattern.true_id, band.true_id, b => { b.max_cr = +changed; })}
              />
            </FlexBox>
            <FlexBox xs_expand={0}>
              <StringBox name="Probability Weight" type="number" value={band.weight} disabled={disabled}
                onBlur={(changed: string) => updateBand(pattern.true_id, band.true_id, b => { b.weight = Math.max(+changed, 0); })}
              />
            </FlexBox>
            <FlexBox xs_expand={0} style={{ width: "20%" }}>
              <SelectStringBox name="Min Rarity" options={RARITY_OPTIONS} value={`${band.min_rarity}`} auto_sort={false} disabled={disabled}
                onChange={(changed: string) => updateBand(pattern.true_id, band.true_id, b => { b.min_rarity = +changed; })}
              />
            </FlexBox>
            <FlexBox xs_expand={0} style={{ width: "20%" }}>
              <SelectStringBox name="Max Rarity" options={RARITY_OPTIONS} value={`${band.max_rarity}`} auto_sort={false} disabled={disabled}
                onChange={(changed: string) => updateBand(pattern.true_id, band.true_id, b => { b.max_rarity = +changed; })}
              />
            </FlexBox>
            <FlexBox className="centered float-right">
              <DeleteButton no_fab disabled={disabled}
                onClick={() => updatePattern(pattern.true_id, p => { p.bands = p.bands.filter(b => b.true_id !== band.true_id); })}
              />
            </FlexBox>
          </FlexBox>
        </FlexBox>
      ))}
      <FlexBox direction="row">
        <FlexBox>
          <DandDButtonBox name="Add Band" disabled={disabled}
            onClick={() => updatePattern(pattern.true_id, p => { p.bands.push(new LootPatternBand()); })}
          />
        </FlexBox>
        <FlexBox>
          <DandDButtonBox name="Reset to Tiers" disabled={disabled}
            onClick={() => updatePattern(pattern.true_id, p => { p.bands = DEFAULT_BANDS.map(o => new LootPatternBand(o)); })}
          />
        </FlexBox>
      </FlexBox>
    </FlexBox>
  );

  const renderPreviewDetails = (preview: DamageTypePreview) => (
    <FlexBox direction="column">
      { preview.bands.map((band_preview, band_index) => {
        const creatures = [...band_preview.creatures].sort((a, b) => a.cr - b.cr || a.name.localeCompare(b.name));
        const items = [...band_preview.items].sort((a, b) => a.rarity - b.rarity || a.name.localeCompare(b.name));
        return (
          <FlexBox key={band_index} className="padded bordered">
            <FlexBox direction="row">
              <FlexBox xs_expand={0} style={{ width: "20%" }}>
                <b>{ bandLabel(band_preview.band) }</b>{ !band_preview.band.enabled ? " (off)" : "" }
              </FlexBox>
              <FlexBox>
                <div className="small_label gray">{ creatures.length } creature(s)</div>
                { creatures.slice(0, LIST_LIMIT).map(o => (
                  <div key={o.id}>{ o.name } <span className="small_label gray">({ o.creature_type }, CR { crString(o.cr) })</span></div>
                ))}
                { creatures.length > LIST_LIMIT && <div className="small_label gray">and { creatures.length - LIST_LIMIT } more</div> }
              </FlexBox>
              <FlexBox>
                <div className="small_label gray">{ items.length } item(s)</div>
                { items.slice(0, LIST_LIMIT).map(o => (
                  <div key={o.id}>{ o.name } <span className="small_label gray">({ RARITY_NUM_TO_STR_MAP[`${o.rarity}`] ?? o.rarity })</span></div>
                ))}
                { items.length > LIST_LIMIT && <div className="small_label gray">and { items.length - LIST_LIMIT } more</div> }
              </FlexBox>
            </FlexBox>
          </FlexBox>
        );
      })}
    </FlexBox>
  );

  const renderPreview = (pattern: LootPattern) => {
    if (!analysis) {
      return (
        <FlexBox className="small_label gray">
          Analyze creatures and items above to see what this pattern matches.
        </FlexBox>
      );
    }
    const previews = previewPattern(pattern, creatureFactsFor(analysis, pattern), analysis.items);
    const planned = planEntries(pattern, creatureFactsFor(analysis, pattern), analysis.items);
    const existing = table.entries.filter(o => o.pattern_id === pattern.true_id).length;
    return (
      <FlexBox direction="column">
        <FlexBox className="header">
          Preview
        </FlexBox>
        <FlexBox className="small_label gray">
          Creatures / items that fit each band. Click a damage type to see them.
        </FlexBox>
        <FlexBox direction="row">
          <FlexBox xs_expand={0} style={{ width: "200px" }}><b>Damage Type</b></FlexBox>
          { pattern.bands.map(band => (
            <FlexBox key={band.true_id} className="centered">
              <b>{ bandLabel(band) }</b>
            </FlexBox>
          ))}
        </FlexBox>
        { previews.map(preview => {
          const key = `${pattern.true_id}:${preview.damage_type}`;
          return (
            <FlexBox direction="column" key={key}>
              <FlexBox direction="row">
                <FlexBox xs_expand={0} className="clickable" style={{ width: "200px" }} onClick={() => toggle(key)}>
                  { preview.damage_type } { expanded.includes(key) ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" /> }
                </FlexBox>
                { preview.bands.map((band_preview, band_index) => {
                  const empty = band_preview.creatures.length === 0 || band_preview.items.length === 0;
                  return (
                    <FlexBox key={band_index}
                      className={empty || !band_preview.band.enabled ? "centered gray" : "centered"}>
                      { band_preview.creatures.length } / { band_preview.items.length }
                    </FlexBox>
                  );
                })}
              </FlexBox>
              { expanded.includes(key) &&
                <FlexBox>
                  { renderPreviewDetails(preview) }
                </FlexBox>
              }
            </FlexBox>
          );
        })}
        <FlexBox>
          <DandDButtonBox
            name={`Generate ${planned.length} Entries${existing > 0 ? ` (Replacing ${existing})` : ""}`}
            disabled={disabled}
            onClick={() => generate(pattern)}
          />
        </FlexBox>
        { existing > 0 &&
          <FlexBox>
            <DandDButtonBox
              name={`Remove the ${existing} Entries This Pattern Made`}
              disabled={disabled}
              onClick={() => onChange(o => { o.entries = o.entries.filter(e => e.pattern_id !== pattern.true_id); })}
            />
          </FlexBox>
        }
      </FlexBox>
    );
  };

  const renderPatternEditor = (pattern: LootPattern) => (
    <FlexBox direction="column">
      <FlexBox direction="row">
        <FlexBox>
          <StringBox name="Name" value={pattern.name} disabled={disabled}
            onBlur={(changed: string) => updatePattern(pattern.true_id, p => { p.name = changed; })}
          />
        </FlexBox>
        <FlexBox>
          <SelectStringBox name="Damage Types" options={TRUE_DAMAGE_TYPES} values={pattern.damage_types} multiple disabled={disabled}
            onChange={(changed: DamageType[]) => updatePattern(pattern.true_id, p => { p.damage_types = changed; })}
          />
        </FlexBox>
        <FlexBox>
          <DandDButtonBox name="Magical Only" disabled={disabled}
            onClick={() => updatePattern(pattern.true_id, p => { p.damage_types = [...MAGICAL_DAMAGE_TYPES] as DamageType[]; })}
          />
        </FlexBox>
      </FlexBox>
      <FlexBox className="header">
        Creatures
      </FlexBox>
      <FlexBox direction="row">
        <FlexBox>
          <SelectStringBox name="Creatures That" options={["Deals", "Resists"]} value={pattern.creature_relation} auto_sort={false} disabled={disabled}
            onChange={(changed: string) => updatePattern(pattern.true_id, p => { p.creature_relation = changed === "Resists" ? "Resists" : "Deals"; })}
          />
        </FlexBox>
        <FlexBox className="small_label gray">
          { pattern.creature_relation === "Deals" ? "...deal the damage type" : "...resist or are immune to the damage type" }.
          Use the rules below to narrow them down more, like by creature type.
          The damage type and CR come from the pattern, so leave those empty here.
        </FlexBox>
      </FlexBox>
      <FlexBox>
        <CreatureLimitInput
          obj={pattern.creature_limit}
          disabled={disabled}
          ignore_knowledge
          ignore_cr
          onChange={(changed: CreatureLimitation) => updatePattern(pattern.true_id, p => { p.creature_limit = changed; })}
        />
      </FlexBox>
      <FlexBox className="header">
        Items
      </FlexBox>
      <FlexBox direction="row">
        <FlexBox>
          <SelectStringBox name="Items That" options={["Adds", "Protects Against"]} value={pattern.item_relation} auto_sort={false} disabled={disabled}
            onChange={(changed: string) => updatePattern(pattern.true_id, p => { p.item_relation = changed === "Protects Against" ? "Protects Against" : "Adds"; })}
          />
        </FlexBox>
        <FlexBox>
          <SelectStringBox name="Item Types (empty for any)" options={ITEM_TYPES} values={pattern.item_types} multiple disabled={disabled}
            onChange={(changed: string[]) => updatePattern(pattern.true_id, p => { p.item_types = changed; })}
          />
        </FlexBox>
        <FlexBox>
          <DiceRollFields name="Items per Drop" dice={pattern.items_per_drop} disabled={disabled}
            onChange={update => updatePattern(pattern.true_id, p => update(p.items_per_drop))}
          />
        </FlexBox>
      </FlexBox>
      <FlexBox>
        { renderBands(pattern) }
      </FlexBox>
      <FlexBox>
        { renderPreview(pattern) }
      </FlexBox>
    </FlexBox>
  );

  return (
    <FlexBox direction="column">
      <FlexBox className="header">
        Patterns ({ table.patterns.length })
      </FlexBox>
      <FlexBox className="small_label gray">
        Patterns pair creatures with items by damage type, like creatures that deal Fire damage dropping weapons
        that add Fire damage. Each one makes an entry for each damage type and CR band. Generating again replaces
        the entries a pattern made before, so you can adjust it and regenerate.
      </FlexBox>
      <FlexBox>
        { progress ?
          <>
            <div>{ progress.message }</div>
            <ProgressBar color="Blue" max={1} value={progress.fraction} />
          </>
        :
          <DandDButtonBox
            name={analysis ? `Analyze Again (${analysis.creatures.length} creatures, ${analysis.items.length} items found)` : "Analyze Creatures and Items"}
            onClick={analyze}
          />
        }
      </FlexBox>
      { !analysis && !progress &&
        <FlexBox className="small_label gray">
          This loads every magic item keyword in full to work out which add damage and which protect against it,
          so it takes a while. It only needs to be done once per session. Run Refresh Light Data on the creature
          page first so creatures know what damage they deal.
        </FlexBox>
      }
      <FlexBox direction="row">
        <FlexBox>
          <DandDButtonBox name="Add Weapons That Add It" disabled={disabled}
            onClick={() => onChange(o => { o.patterns.push(newPattern("Elemental Weapons", "Adds", ["Weapon"])); })}
          />
        </FlexBox>
        <FlexBox>
          <DandDButtonBox name="Add Items That Protect" disabled={disabled}
            onClick={() => onChange(o => { o.patterns.push(newPattern("Protection", "Protects Against", [])); })}
          />
        </FlexBox>
        <FlexBox>
          <DandDButtonBox name="Add Blank Pattern" disabled={disabled}
            onClick={() => onChange(o => { o.patterns.push(newPattern("New Pattern", "Adds", [])); })}
          />
        </FlexBox>
      </FlexBox>
      { message !== "" &&
        <FlexBox>{ message }</FlexBox>
      }
      <FlexBox direction="column">
        { table.patterns.map(pattern => {
          const is_expanded = expanded.includes(pattern.true_id);
          const generated = table.entries.filter(o => o.pattern_id === pattern.true_id).length;
          return (
            <FlexBox direction="column" key={pattern.true_id} className="padded bordered">
              <FlexBox direction="row">
                <FlexBox className="clickable" xs_expand={0} style={{ width: "20%" }} onClick={() => toggle(pattern.true_id)}>
                  <div className="header">{ pattern.name === "" ? "(Unnamed)" : pattern.name }</div>
                  <div className="small_label gray">
                    Creatures that { pattern.creature_relation === "Deals" ? "deal" : "resist" } { pattern.damage_types.length } damage type(s)
                    { " → " }{ pattern.item_types.length > 0 ? pattern.item_types.join("/") : "items" } that
                    { pattern.item_relation === "Adds" ? " add it" : " protect against it" }.
                    { " " }{ pattern.bands.filter(o => o.enabled).length } band(s), { generated } entries made.
                  </div>
                </FlexBox>
                <FlexBox className="centered" xs_expand={0} style={{ width: "20%" }}>
                  <CopyButton disabled={disabled}
                    onClick={() => onChange(o => {
                      const copy = new LootPattern({ ...pattern.toDBObj(), true_id: undefined, name: `${pattern.name} (Copy)` });
                      copy.bands = copy.bands.map(b => new LootPatternBand({ ...b.toDBObj(), true_id: undefined }));
                      o.patterns.push(copy);
                    })}
                  />
                  <DeleteButton no_fab disabled={disabled}
                    onClick={() => onChange(o => { o.patterns = o.patterns.filter(p => p.true_id !== pattern.true_id); })}
                  />
                  <span className="clickable" onClick={() => toggle(pattern.true_id)}>
                    { is_expanded ? <ExpandLess /> : <ExpandMore /> }
                  </span>
                </FlexBox>
              </FlexBox>
              { is_expanded &&
                <FlexBox className="padded">
                  { renderPatternEditor(pattern) }
                </FlexBox>
              }
            </FlexBox>
          );
        })}
      </FlexBox>
    </FlexBox>
  );
}
